import React, { useState } from 'react';
import { useAuth } from '../context/shared/AuthContext';
import { useTheme } from '../context/shared/ThemeContext';
import { Modal } from './common/Modal';
import type { Message } from '../../shared/types/src';
import apiService from '../services/shared/apiService';

interface SaveMemoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  messages: { aiMessage: Message, userPrompt?: Message };
  onSave: () => void;
}

const SaveMemoryModal: React.FC<SaveMemoryModalProps> = ({ isOpen, onClose, messages, onSave }) => {
  const { themeClasses } = useTheme();
  const { user } = useAuth();
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!user) {
      console.error("User not logged in");
      return;
    }

    setIsSaving(true);
    try {
      await apiService.saveMemory(user.uid, messages.aiMessage.text, notes ? [notes] : []);
      onSave(); // This could trigger a refetch or a toast in the parent
      onClose();
    } catch (err) {
      console.error('Save operation failed:', err);
      // In a real app, show an error toast to the user
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className={`p-4 ${themeClasses.cardBg}`}>
        <h3 className={`text-lg font-semibold mb-4 ${themeClasses.primaryText}`}>Save to Memory</h3>
        <div className="mb-4">
          <p className={`${themeClasses.secondaryText}`}>AI Response:</p>
          <div className={`mt-1 p-3 rounded-lg bg-gray-100 dark:bg-gray-700 ${themeClasses.primaryText}`}>
            {messages.aiMessage.text}
          </div>
        </div>

        <div className="mb-4">
          <label htmlFor="notes" className={`block text-sm font-medium ${themeClasses.primaryText}`}>
            Add Notes or Tags (optional)
          </label>
          <input
            type="text"
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g., #daily_reflection, #idea"
            className={`w-full mt-1 ${themeClasses.input}`}
          />
        </div>

        <div className="flex justify-end space-x-2 mt-6">
          <button onClick={onClose} className={themeClasses.buttonSecondary}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={isSaving} className={themeClasses.buttonPrimary}>
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SaveMemoryModal; 